import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  Banknote,
  Box,
  CircleDollarSign,
  Pause,
  TrendingDown,
  TrendingUp,
  Zap,
} from "lucide-react";
import {
  getUserDeployments,
  getUserPnlDaily,
  getUserPnlSummary,
  type AdminUserDetail,
  type OversightMode,
} from "../../api/admin";
import type { ActiveDeploymentSnapshot } from "../../api/dashboard";
import type { Deployment } from "../../api/deployments";
import AdminStopKillDialog, {
  type AdminAction,
} from "../../components/AdminStopKillDialog";
import Button from "../../components/Button";
import Card from "../../components/Card";
import Chip from "../../components/Chip";
import DataTable from "../../components/DataTable";
import KpiTile from "../../components/KpiTile";
import PnlCalendar from "../../components/PnlCalendar";
import Skeleton from "../../components/Skeleton";
import StatusPill from "../../components/StatusPill";
import ToggleGroup from "../../components/ToggleGroup";
import { formatINR, formatInt, signedClass } from "../../lib/format";
import UserBrokersCard from "./UserBrokersCard";

const RUNNING_STATUSES = new Set(["RUNNING", "STARTING", "STOPPING"]);
const IST_OFFSET_MS = 19_800_000;

function istTodayKey(): string {
  return new Date(Date.now() + IST_OFFSET_MS).toISOString().slice(0, 10);
}

/** Admin oversight of one user's trading: P&L tiles, a daily P&L calendar and
 *  their deployments, all behind one All / Paper / Live switch. Read-only
 *  except Stop / Kill, which confirm first and notify the user. */
export default function UserTradingPanel({ user }: { user: AdminUserDetail }) {
  const [mode, setMode] = useState<OversightMode | undefined>(undefined);
  const ownerLabel = user.display_name || user.email;

  const { data: summary, isLoading } = useQuery({
    queryKey: ["admin-user-summary", user.id, mode ?? "all"],
    queryFn: () => getUserPnlSummary(user.id, mode),
    refetchInterval: 3_000,
  });
  const { data: days } = useQuery({
    queryKey: ["admin-user-pnl-daily", user.id, mode ?? "all"],
    queryFn: () => getUserPnlDaily(user.id, mode),
    refetchInterval: 30_000,
  });
  const { data: deployments } = useQuery({
    queryKey: ["admin-user-deployments", user.id, mode ?? "all"],
    queryFn: () => getUserDeployments(user.id, mode),
    refetchInterval: 5_000,
  });

  const [action, setAction] = useState<{ d: Deployment; action: AdminAction } | null>(
    null,
  );

  const k = summary?.kpis;
  const showLive = mode !== "paper";
  const showPaper = mode !== "live";
  const unrealized =
    (showLive ? Number(k?.unrealized_live ?? 0) : 0) +
    (showPaper ? Number(k?.unrealized_paper ?? 0) : 0);
  const openPositions =
    (showLive ? (k?.open_positions ?? 0) : 0) +
    (showPaper ? (k?.open_positions_paper ?? 0) : 0);

  const activeById = useMemo(() => {
    const m = new Map<string, ActiveDeploymentSnapshot>();
    for (const a of summary?.active_deployments ?? []) m.set(a.id, a);
    return m;
  }, [summary]);

  // Calendar window: the last 12 months, or since the first fill if later.
  const calendar = useMemo(() => {
    const today = istTodayKey();
    const yearAgo = new Date(Date.now() + IST_OFFSET_MS - 365 * 86_400_000)
      .toISOString()
      .slice(0, 10);
    const first = days?.[0]?.day;
    const start = first && first > yearAgo ? first : yearAgo;
    return {
      start,
      end: today,
      rows: (days ?? []).filter((d) => d.day >= start),
    };
  }, [days]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted">
          {mode === "paper"
            ? "Paper deployments only."
            : mode === "live"
              ? "Live deployments only."
              : `All of ${ownerLabel}'s deployments.`}
        </p>
        <ToggleGroup
          value={mode ?? ""}
          onChange={(v) => setMode(v === "paper" || v === "live" ? v : undefined)}
          options={[
            { value: "", label: "All" },
            { value: "paper", label: "Paper" },
            { value: "live", label: "Live" },
          ]}
        />
      </div>

      {isLoading && !summary ? (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-28" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <KpiTile
            label="P&L today"
            value={formatINR(k?.total_pnl_today, { sign: true })}
            valueRaw={Number(k?.total_pnl_today ?? 0)}
            tone="pnl"
            sub={
              <span>
                Realized{" "}
                <span className={signedClass(k?.realized_today ?? 0)}>
                  {formatINR(k?.realized_today, { sign: true })}
                </span>
              </span>
            }
            icon={<CircleDollarSign size={14} />}
          />
          <KpiTile
            label="Overall P&L (realized)"
            value={formatINR(k?.realized_all_time, { sign: true })}
            valueRaw={Number(k?.realized_all_time ?? 0)}
            tone="pnl"
            icon={
              Number(k?.realized_all_time ?? 0) >= 0 ? (
                <TrendingUp size={14} />
              ) : (
                <TrendingDown size={14} />
              )
            }
          />
          <KpiTile
            label="Unrealized"
            value={formatINR(unrealized, { sign: true })}
            valueRaw={unrealized}
            tone="pnl"
            icon={<Banknote size={14} />}
          />
          <KpiTile
            label="Open positions"
            value={formatInt(openPositions)}
            icon={<Box size={14} />}
          />
          <KpiTile
            label="Deployments"
            value={formatInt(k?.deployments_total)}
            sub={<span>{formatInt(k?.running_deployments)} running</span>}
            icon={<Activity size={14} />}
            tone="brand"
          />
        </div>
      )}

      <Card
        title="Daily P&L"
        subtitle="Realized P&L per IST day, from recorded fills (fees included)."
      >
        {calendar.rows.length === 0 ? (
          <div className="text-sm text-muted">No fills yet.</div>
        ) : (
          <PnlCalendar
            days={calendar.rows}
            startTs={`${calendar.start}T00:00:00`}
            endTs={`${calendar.end}T00:00:00`}
          />
        )}
      </Card>

      <Card
        title="Deployments"
        subtitle="Open one for its chart, positions, orders, fills and config (read-only). You can stop or kill a running deployment, but not start it."
      >
        <DataTable
          rows={deployments ?? []}
          rowKey={(d) => d.id}
          empty={<div className="text-sm text-muted">No deployments.</div>}
          columns={[
            {
              key: "name",
              header: "Name",
              render: (d) => (
                <Link
                  to={`/deployments/${d.id}`}
                  className="text-brand-600 dark:text-brand-400 hover:underline"
                >
                  {d.name}
                </Link>
              ),
            },
            {
              key: "strategy",
              header: "Strategy",
              mono: true,
              render: (d) => d.strategy_key,
            },
            {
              key: "mode",
              header: "Mode",
              render: (d) => (
                <Chip tone={d.mode === "live" ? "warn" : "info"} mono>
                  {d.mode}
                </Chip>
              ),
            },
            {
              key: "status",
              header: "Status",
              render: (d) => <StatusPill status={d.status} />,
            },
            {
              key: "today",
              header: "Realized today",
              align: "right",
              mono: true,
              render: (d) => {
                const a = activeById.get(d.id);
                if (!a) return <span className="text-muted">—</span>;
                const v = a.day_realized_pnl ?? a.realized_pnl;
                return (
                  <span className={signedClass(v)}>{formatINR(v, { sign: true })}</span>
                );
              },
            },
            {
              key: "unrealized",
              header: "Unrealized",
              align: "right",
              mono: true,
              render: (d) => {
                const a = activeById.get(d.id);
                if (!a) return <span className="text-muted">—</span>;
                return (
                  <span className={signedClass(a.unrealized_pnl)}>
                    {formatINR(a.unrealized_pnl, { sign: true })}
                  </span>
                );
              },
            },
            {
              key: "open",
              header: "Open pos.",
              align: "right",
              render: (d) => activeById.get(d.id)?.open_positions ?? "—",
            },
            {
              key: "actions",
              header: "",
              align: "right",
              render: (d) =>
                RUNNING_STATUSES.has(d.status) ? (
                  <div className="flex justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="secondary"
                      iconLeft={<Pause size={12} />}
                      onClick={() => setAction({ d, action: "stop" })}
                    >
                      Stop
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      iconLeft={<Zap size={12} />}
                      onClick={() => setAction({ d, action: "kill" })}
                    >
                      Kill
                    </Button>
                  </div>
                ) : null,
            },
          ]}
        />
      </Card>

      <UserBrokersCard userId={user.id} ownerLabel={ownerLabel} />

      {action && (
        <AdminStopKillDialog
          target={{
            id: action.d.id,
            name: action.d.name,
            mode: action.d.mode,
            ownerLabel,
          }}
          action={action.action}
          onClose={() => setAction(null)}
        />
      )}
    </div>
  );
}
